import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { getSessions, getSoftRooms, createSession, completeSession } from '@/lib/api'
import { formatDateTime } from '@/lib/utils'
import { useToast } from '@/hooks/use-toast'
import { useSessionContext } from '@/components/SessionProvider'
import { Plus, Users, Check, ChevronRight, User, Building2, AlertCircle, CheckCircle2 } from 'lucide-react'
import { cn } from '@/lib/utils'

export default function Sessions() {
  const [showForm, setShowForm] = useState(false)
  const [filter, setFilter] = useState<string>('')
  const [submitted, setSubmitted] = useState(false)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { session: authSession } = useSessionContext()

  const { data: sessions, isLoading } = useQuery({
    queryKey: ['sessions', { status: filter }],
    queryFn: () => getSessions({ status: filter || undefined }),
  })

  const { data: softRooms } = useQuery({
    queryKey: ['softRooms'],
    queryFn: getSoftRooms,
  })

  const createMutation = useMutation({
    mutationFn: createSession,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sessions'] })
      queryClient.invalidateQueries({ queryKey: ['stats'] })
      setSubmitted(true)
      setTimeout(() => {
        setSubmitted(false)
        setShowForm(false)
      }, 2000)
      toast({ title: 'Session started' })
    },
    onError: (error: Error) => {
      toast({ title: 'Failed to start session', description: error.message, variant: 'destructive' })
    },
  })

  const completeMutation = useMutation({
    mutationFn: (sessionId: string) => completeSession(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sessions'] })
      queryClient.invalidateQueries({ queryKey: ['stats'] })
      toast({ title: 'Session completed' })
    },
    onError: (error: Error) => {
      toast({ title: 'Failed to complete session', description: error.message, variant: 'destructive' })
    },
  })

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const data = {
      soft_room_id: formData.get('soft_room_id') || undefined,
      case_number: formData.get('case_number'),
      victim_first_name: formData.get('victim_first_name'),
      victim_last_name: formData.get('victim_last_name'),
      victim_phone: formData.get('victim_phone') || undefined,
      victim_dob: formData.get('victim_dob') || undefined,
      victim_nationality: formData.get('victim_nationality') || undefined,
      victim_gender: formData.get('victim_gender'),
      is_minor: formData.get('is_minor') === 'true',
      needs_medical: formData.get('needs_medical') === 'on',
      needs_interpreter: formData.get('needs_interpreter') === 'on',
      intake_notes: formData.get('intake_notes'),
    }
    createMutation.mutate(data)
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Victim Sessions</h1>
          <p className="text-sm text-muted-foreground">Intake and service documentation</p>
        </div>
        <Button size="sm" onClick={() => setShowForm(!showForm)} className="gap-1.5">
          <Plus className="h-3.5 w-3.5" />
          New Session
        </Button>
      </div>

      {/* Filter */}
      <div className="flex gap-2">
        {['', 'in_progress', 'completed'].map((status) => (
          <Button
            key={status}
            variant={filter === status ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(status)}
          >
            {status === '' ? 'All' : status === 'in_progress' ? 'Active' : 'Completed'}
          </Button>
        ))}
      </div>

      {/* New Session Form */}
      {showForm && (
        submitted ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="bg-primary/10 p-6 rounded-full mb-6">
              <CheckCircle2 size={48} className="text-primary" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Session Started</h2>
            <p className="text-muted-foreground text-center max-w-sm">
              Victim intake session has been created. You can now add services from the session detail page.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Responder Card */}
            <Card className="bg-card border-border shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Responding As</p>
                    <p className="font-semibold">{authSession?.user?.name || 'Unknown'}</p>
                    <p className="text-sm text-muted-foreground">{authSession?.user?.email}</p>
                  </div>
                  <div className="bg-primary/10 p-3 rounded-full">
                    <User size={24} className="text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Alert Banner */}
            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="p-4 flex gap-3">
                <AlertCircle size={20} className="text-primary shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-bold text-sm text-foreground">New Victim Session</h3>
                  <p className="text-xs text-muted-foreground">
                    All information is encrypted and securely stored. Only authorized responders can access session data.
                  </p>
                </div>
              </CardContent>
            </Card>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Location & Case */}
              <Card className="bg-card border-border shadow-sm">
                <CardHeader className="pb-4">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Building2 size={16} className="text-primary" />
                    Location & Case
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-muted-foreground uppercase">Soft Room</label>
                      <select name="soft_room_id" className="w-full px-3 py-2 border rounded-lg bg-secondary border-border text-sm">
                        <option value="">Select a room...</option>
                        {softRooms?.map((room: any) => (
                          <option key={room.id} value={room.id}>
                            {room.name} ({room.airport_code})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-muted-foreground uppercase">Case Number</label>
                      <input
                        name="case_number"
                        className="w-full px-3 py-2 border rounded-lg bg-secondary border-border text-sm"
                        placeholder="FBI-2024-XXXX"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Victim Information */}
              <Card className="bg-card border-border shadow-sm">
                <CardHeader className="pb-4">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <User size={16} className="text-primary" />
                    Victim Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-muted-foreground uppercase">First Name</label>
                      <input
                        name="victim_first_name"
                        className="w-full px-3 py-2 border rounded-lg bg-secondary border-border text-sm"
                        placeholder="First name"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-muted-foreground uppercase">Last Name</label>
                      <input
                        name="victim_last_name"
                        className="w-full px-3 py-2 border rounded-lg bg-secondary border-border text-sm"
                        placeholder="Last name"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-muted-foreground uppercase">Phone Number</label>
                      <input
                        name="victim_phone"
                        type="tel"
                        className="w-full px-3 py-2 border rounded-lg bg-secondary border-border text-sm"
                        placeholder="(555) 000-0000"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-muted-foreground uppercase">Date of Birth</label>
                      <input
                        name="victim_dob"
                        type="date"
                        className="w-full px-3 py-2 border rounded-lg bg-secondary border-border text-sm"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-muted-foreground uppercase">Nationality</label>
                      <input
                        name="victim_nationality"
                        className="w-full px-3 py-2 border rounded-lg bg-secondary border-border text-sm"
                        placeholder="Country of origin"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-muted-foreground uppercase">Gender</label>
                      <select name="victim_gender" className="w-full px-3 py-2 border rounded-lg bg-secondary border-border text-sm">
                        <option value="">Unknown</option>
                        <option value="female">Female</option>
                        <option value="male">Male</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-muted-foreground uppercase">Is Minor?</label>
                      <select name="is_minor" className="w-full px-3 py-2 border rounded-lg bg-secondary border-border text-sm">
                        <option value="">Unknown</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-4 pt-2">
                    <label className={cn(
                      'flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors',
                      'bg-secondary border-border hover:bg-muted'
                    )}>
                      <input type="checkbox" name="needs_medical" className="rounded" />
                      <span className="text-xs font-semibold">Needs Medical</span>
                    </label>
                    <label className={cn(
                      'flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors',
                      'bg-secondary border-border hover:bg-muted'
                    )}>
                      <input type="checkbox" name="needs_interpreter" className="rounded" />
                      <span className="text-xs font-semibold">Needs Interpreter</span>
                    </label>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-muted-foreground uppercase">Intake Notes</label>
                    <textarea
                      name="intake_notes"
                      rows={3}
                      className="w-full px-3 py-2 border rounded-lg bg-secondary border-border text-sm"
                      placeholder="Initial observations, circumstances of encounter..."
                    />
                  </div>
                </CardContent>
              </Card>

              <div className="flex gap-2">
                <Button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="flex-1 py-5 text-base font-bold gap-2"
                >
                  <CheckCircle2 size={18} />
                  {createMutation.isPending ? 'Starting...' : 'Start Session'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        )
      )}

      {/* Sessions List */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : sessions && sessions.length > 0 ? (
        <div className="space-y-3">
          {sessions.map((session: any) => (
            <Card
              key={session.id}
              className="cursor-pointer transition-shadow hover:shadow-md"
              onClick={() => navigate(`/sessions/${session.id}`)}
            >
              <CardContent className="p-4">
                <div className="flex justify-between items-start gap-3">
                  <div className="flex gap-3 min-w-0 flex-1">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted">
                      <Users className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-sm">
                        {session.victim_first_name || 'Unknown'}{' '}
                        {session.victim_last_name || ''}
                        {session.case_number && (
                          <span className="text-muted-foreground ml-2">
                            #{session.case_number}
                          </span>
                        )}
                      </h3>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Responder: {session.responder_name}
                        {session.responder_agency && ` (${session.responder_agency})`}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Started: {formatDateTime(session.started_at)}
                      </p>
                      {session.services && session.services.length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {session.services.map((s: any, i: number) => (
                            <span key={i} className="sanctuary-badge sanctuary-badge-low">
                              {s.type}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={session.status === 'completed' ? 'sanctuary-badge sanctuary-badge-completed' : 'sanctuary-badge sanctuary-badge-active'}>
                      {session.status === 'in_progress' ? 'Active' : session.status}
                    </span>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </div>
                {session.status === 'in_progress' && (
                  <div className="mt-3 ml-13">
                    <Button
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation()
                        completeMutation.mutate(session.id)
                      }}
                      className="gap-1"
                    >
                      <Check className="h-3.5 w-3.5" />
                      Complete Session
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Users className="h-10 w-10 mb-3 opacity-40" />
            <p className="font-medium">No sessions found</p>
            <p className="text-sm mt-1">Start a new session to begin intake</p>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
